import React, { useEffect } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';

import styles from './Compliance.module.scss';
import Button from '../../../../components/button/button';
import FormCheckbox from '../../../../components/formCheckbox/FormCheckbox';
import FormNewCheckbox from '../../../../components/formCheckbox/FormNewCheckbox';
import Portal from '../../../../components/portal/Portal';
import StepperNavigation from '../../../../components/stepper/StepperNavigation';
import SectionContainer from '../../../../components/template/SectionContainer/SectionContainer';
import Subtitle from '../../../../components/template/Subtitle/Subtitle';
import Title from '../../../../components/template/Title/Title';
import { QuestionForm } from '../../../../models/questionModel';
import useFormStore from '../../../../store/FormStore';
import useQuestionStore from '../../../../store/QuestionStore';
import useStepperStore from '../../../../store/StepperStore';
import MainQuestion from '../MainQuestion/MainQuestion';

interface ComplianceProps {}

const Compliance: React.FC<ComplianceProps> = ({}) => {
  const { setQuestion } = useQuestionStore();
  const { setStepper } = useStepperStore();
  const { setForm } = useFormStore();

  //#region Form
  const formMethods = useFormContext();
  const {
    getValues,
    reset,
    watch,
    formState: { isValid },
  } = formMethods;
  const { compliance } = watch();
  //#endregion

  useEffect(() => {
    reset(getValues());
  }, [compliance?.compulsory]);

  //#region Handler
  const getQuesWithMarks = (
    impactTitle:
      | 'Bursa listing requirement'
      | 'Questions relating to the effectiveness of the internal audit function'
  ) => {
    const selectedImpact = ComplianceData.impactQuestions.find(
      (impact) => impact.title === impactTitle
    );

    const getQuesWithMarksBySection = (
      sectionId: 'compliance.bursa' | 'compliance.internalaudit'
    ) => {
      return selectedImpact?.questions.map((ques, quesIdx) => {
        const selectedCheckbox = ques.checkbox.find(
          (check, idx) =>
            `${check.marks}_${idx}` == getValues(sectionId)[quesIdx]
        );

        const selectedAnswer = selectedCheckbox?.label;

        return {
          question: ques.question,
          marks: selectedCheckbox?.marks || 0,
          answer: selectedAnswer || '',
        };
      });
    };

    switch (impactTitle) {
      case 'Bursa listing requirement':
        return getQuesWithMarksBySection('compliance.bursa');
      case 'Questions relating to the effectiveness of the internal audit function':
        return getQuesWithMarksBySection('compliance.internalaudit');
    }
  };

  const onHandleNavigation = (nav: 'next' | 'prev') => {
    if (!isValid && nav == 'next') return;

    const compulsoryQuestions = ComplianceData.compulsoryQuestions.map(
      (comp) => ({
        answer: getValues('compliance.compulsory'),
        question: comp.question,
        questionNotes: [],
        answerNotes: [],
      })
    );

    const impactQuestions = ComplianceData.impactQuestions.flatMap(
      (imp) =>
        getQuesWithMarks(imp.title as any)?.map((q) => ({
          ...q,
          answerNotes: [],
          questionNotes: [],
        })) || []
    );

    const probabilityQuestions = ComplianceData.probabilityQuestions.map(
      (comp) => ({
        answer:
          comp.inputs.find(
            (inp) => inp.value == Number(getValues('compliance.probability'))
          )?.label || '',
        question: comp.question,
        questionNotes: [],
        answerNotes: [],
        marks: Number(getValues('compliance.probability')),
      })
    );

    const finalComplianceData = new QuestionForm(
      ComplianceData.title,
      ComplianceData.description,
      compulsoryQuestions,
      impactQuestions,
      probabilityQuestions
    );

    setQuestion({ compliance: finalComplianceData });

    setForm({ ...getValues() });

    setStepper(
      nav,
      undefined,
      compliance?.compulsory == '' ? undefined : compliance?.compulsory == 'Yes'
    );
  };

  const generateRandomData = (data: any) => {
    const result: any = {
      compulsory: Math.random() > 0.5 ? 'Yes' : 'Not applicable',
      probability: Math.floor(Math.random() * 5) + 1,
    };

    data.forEach((section: any) => {
      let key = '';
      if (section.title.toLowerCase().includes('bursa')) {
        key = 'bursa';
      } else if (section.title.toLowerCase().includes('internal audit')) {
        key = 'internalaudit';
      }

      result[key] = section.questions.map((q: any) => {
        const randomIndex = Math.floor(Math.random() * q.checkbox.length);
        const option = q.checkbox[randomIndex];
        return `${option.marks}_${randomIndex}`;
      });
    });

    return result;
  };
  //#endregion

  const ComplianceData = {
    title: 'Compliance Risk',
    description:
      'Compliance risk refers to the possibility of an organization facing legal penalties, financial losses, or reputational damage due to non-compliance with laws, regulations, industry standards, or internal policies.',
    compulsoryQuestions: [
      {
        id: 'compliance.compulsory',
        question: 'Is your Company exposed to compliance risk?',
        inputs: [
          { value: 'Yes', label: 'Yes' },
          { value: 'Not applicable', label: 'Not applicable' },
        ],
      },
    ],
    probabilityQuestions: [
      {
        id: 'compliance.probability',
        question: 'How frequent is the Company facing compliance risk?',
        inputs: [
          { value: 1, label: 'Improbable' },
          { value: 2, label: 'Remote' },
          { value: 3, label: 'Fairly Likely' },
          { value: 4, label: 'Probable' },
          { value: 5, label: 'Very Likely' },
        ],
      },
    ],
    impactQuestions: [
      {
        title: 'Bursa listing requirement',
        questions: [
          {
            id: 'compliance.bursa.0',
            question: 'Has the Company been listed in Bursa Malaysia?',
            checkbox: [
              { label: 'Yes, in the Main Market', marks: 5 },
              { label: 'Yes, in the ACE Market', marks: 5 },
              { label: 'Yes, in the LEAP Market', marks: 5 },
              { label: 'No', marks: 3 },
            ],
          },
          {
            id: 'compliance.bursa.1',
            question:
              'Has the Company met the Admission Criteria (i.e. Profit Test, Market Capitalisation Test & Infrastructure Project Corporation Test)?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No, as not required in ACE / LEAP Market', marks: 3 },
            ],
          },
          {
            id: 'compliance.bursa.2',
            question: 'Has the Company met the Public Spread requirement?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 3 },
            ],
          },
          {
            id: 'compliance.bursa.3',
            question: 'Has the Company met the Bumiputera Equity Requirement?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No, as not required in LEAP Market', marks: 3 },
            ],
          },
          {
            id: 'compliance.bursa.4',
            question:
              'Has the Company met the Financial Position & Liquidity requirement?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No, as not required in LEAP Market', marks: 3 },
            ],
          },
          {
            id: 'compliance.bursa.5',
            question:
              'Has the Company met the Moratorium on Shares requirement?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 3 },
            ],
          },
        ],
      },
      {
        title:
          'Questions relating to the effectiveness of the internal audit function',
        questions: [
          {
            id: 'compliance.internalaudit.0',
            question:
              'Is the Chief Audit Executive a member of The Institute of Internal Auditors Malaysia?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 1 },
              { label: 'Not applicable', marks: 3 },
            ],
          },
          {
            id: 'compliance.internalaudit.1',
            question:
              'Does the Audit Committee decide on the scope and functions of the internal audit as required in the Listing Requirements?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 1 },
              { label: 'Not applicable', marks: 3 },
            ],
          },
          {
            id: 'compliance.internalaudit.2',
            question:
              'Does the internal audit function understand the company’s business and the peculiarities of the industry(ies) of which the company operates in?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 1 },
              { label: 'Not applicable', marks: 3 },
            ],
          },
          {
            id: 'compliance.internalaudit.3',
            question:
              'Do internal auditors meet with the audit committee without the presence of non-audit committee members whenever deemed necessary in relation to the operations of the company?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 1 },
              { label: 'Not applicable', marks: 3 },
            ],
          },
          {
            id: 'compliance.internalaudit.4',
            question:
              'Does internal audit function perform regular reviews to test the effectiveness of the financial, operational and compliance controls and processes of the company?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 1 },
              { label: 'Not applicable', marks: 3 },
            ],
          },
          {
            id: 'compliance.internalaudit.5',
            question:
              'Does the internal audit function test the effectiveness of risk management framework and policies?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 1 },
              { label: 'Not applicable', marks: 3 },
            ],
          },
          {
            id: 'compliance.internalaudit.6',
            question:
              'Does the internal audit function have sufficient resources and competency to carry out its work?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 1 },
              { label: 'Not applicable', marks: 3 },
            ],
          },
          {
            id: 'compliance.internalaudit.7',
            question:
              'Do the internal auditors undertake their functions according to the standards set by recognised professional bodies?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 1 },
              { label: 'Not applicable', marks: 3 },
            ],
          },
          {
            id: 'compliance.internalaudit.8',
            question:
              'Does the internal audit function provide input into developing action plans to monitor risks and internal controls based on the internal audit plan and processes undertaken?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 1 },
              { label: 'Not applicable', marks: 3 },
            ],
          },
          {
            id: 'compliance.internalaudit.9',
            question:
              'Is the scope of internal audit limited to certain areas only? If so, please state the reason for the limitation.',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 1 },
              { label: 'Not applicable', marks: 3 },
            ],
          },
          {
            id: 'compliance.internalaudit.10',
            question:
              'Does the internal audit function include detection and investigation of fraud? If it does not, please comment its role in relation to investigation of fraud?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 1 },
              { label: 'Not applicable', marks: 3 },
            ],
          },
          {
            id: 'compliance.internalaudit.11',
            question:
              'Has the listed issuer carried out a Quality Assessment Review (QAR) of the internal audit function?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 1 },
              { label: 'Not applicable', marks: 3 },
            ],
          },
          {
            id: 'compliance.internalaudit.12',
            question:
              'Do the listed issuer’s external auditors rely on the internal audit assessment? If not, why?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 1 },
              { label: 'Not applicable', marks: 3 },
            ],
          },
          {
            id: 'compliance.internalaudit.13',
            question:
              'Does the internal audit function work in collaboration with external auditors, particularly in the area of evaluation of internal controls?',
            checkbox: [
              { label: 'Yes', marks: 5 },
              { label: 'No', marks: 1 },
              { label: 'Not applicable', marks: 3 },
            ],
          },
        ],
      },
    ],
  };

  return (
    <FormProvider {...formMethods}>
      <div className={styles.container}>
        <MainQuestion
          id="compliance"
          title={ComplianceData.title}
          description={ComplianceData.description}
          compulsoryQuestions={ComplianceData.compulsoryQuestions}
          probabilityQuestions={ComplianceData.probabilityQuestions}
        />

        {compliance?.compulsory && compliance?.compulsory == 'Yes' && (
          <div>Compliance Risk Assessment Questionnaire</div>
        )}

        {compliance?.compulsory &&
          compliance?.compulsory == 'Yes' &&
          ComplianceData.impactQuestions.map((impact, idx) => (
            <SectionContainer key={idx}>
              <Title>{impact.title}</Title>

              {impact.questions.map((ques, qIdx) => (
                <div key={qIdx}>
                  <div>
                    {qIdx + 1}. {ques.question}
                  </div>
                  {ques.checkbox.map((check, index) => (
                    <FormNewCheckbox
                      key={index}
                      id={ques.id}
                      label={check.label}
                      value={`${check.marks}_${index}`}
                      required
                    />
                  ))}
                </div>
              ))}
            </SectionContainer>
          ))}
      </div>

      <StepperNavigation
        isValid={isValid}
        onPrev={() => onHandleNavigation('prev')}
        onNext={() => onHandleNavigation('next')}
      />

      <Portal root="tester">
        <Button
          isCircle
          onClick={() => {
            reset({
              ...getValues(),
              compliance: {
                ...getValues().compliance,
                ...generateRandomData(ComplianceData.impactQuestions),
              },
            });
          }}
        >
          Random
        </Button>
      </Portal>
    </FormProvider>
  );
};

export default Compliance;
